import { useCallback, useEffect, useRef } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { Checkbox } from 'src/components/atoms/Checkbox';
import ControlledCheckbox from 'src/components/atoms/Checkbox/ControlledCheckbox';
import Gap from 'src/components/atoms/Gap';
import { Icon, IconColor } from 'src/components/atoms/Icon';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { Row } from 'src/components/atoms/Row';
import Text, { TextAlign, TextVariant } from 'src/components/atoms/Text';
import { ToolTip } from 'src/components/atoms/ToolTip';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import { ControlledDatePicker } from 'src/components/molecules/DatePicker';
import { ControlledDropdown } from 'src/components/molecules/Dropdown';
import PDFViewer from 'src/components/molecules/PDFViewer/PDFViewer';
import {
  clearIVFormFields,
  eligibleDocuments,
  logBasePayEntered,
  logNriCheckboxToggled,
  MISMATCH_CODES
} from 'src/features/incomeVerification';
import { docTypeItem, useProofDocumentTypes } from 'src/features/proofs/hooks';

import { EmbedMode } from 'src/ts/enums/pdfViewer';
import { IncomeVerification } from 'src/ts/interfaces/incomeVerification';
import {
  CarouselIncludeRow,
  CarouselMismatchBanner,
  Container,
  NameComparison,
  PdfViewerWrapper
} from './styles';

type IncomeVerificationFormProps = {
  currentDocument: IncomeVerification;
  totalNumberOfDocuments: number;
  onSave: (formData: IncomeVerification) => void;
  goToNextDocument: () => void;
  documentErrors?: number[];
  applicantName?: string;
  documentName?: string;
  isIncludedInCalculation?: boolean;
  onToggleInclude?: () => void;
  entryId?: string;
  incomeVerificationReviewId?: string;
  applicantId?: string;
  companyId?: string;
  propertyId?: string;
};

const IncomeVerificationForm = ({
  currentDocument,
  totalNumberOfDocuments,
  onSave,
  goToNextDocument,
  documentErrors,
  applicantName,
  documentName,
  isIncludedInCalculation,
  onToggleInclude,
  entryId,
  incomeVerificationReviewId,
  applicantId,
  companyId,
  propertyId
}: IncomeVerificationFormProps) => {
  const hasLoggedBasePayRef = useRef(false);
  const signalContext = {
    applicant_id: applicantId,
    entry_id: entryId,
    company_id: companyId,
    property_id: propertyId,
    document_id: currentDocument.id,
    income_verification_review_id: incomeVerificationReviewId
  };
  const methods = useForm<IncomeVerification>({
    defaultValues: currentDocument,
    mode: 'onBlur'
  });

  const { proofDocumentTypes } = useProofDocumentTypes();

  const hasMismatch = documentErrors?.some((code) => MISMATCH_CODES.includes(code)) ?? false;
  const severity = hasMismatch ? (documentErrors?.includes(3003) ? 'error' : 'warning') : undefined;
  const isExcluded = hasMismatch && isIncludedInCalculation === false;

  const handleOnSave = useCallback(
    async (data: IncomeVerification) => {
      data = {
        ...data,
        index: currentDocument.index,
        file: currentDocument.file,
        docId: currentDocument.docId
      };

      if (!hasLoggedBasePayRef.current && data.basePayIncomeAmount) {
        logBasePayEntered(data.basePayIncomeAmount, signalContext);
        hasLoggedBasePayRef.current = true;
      }

      onSave(data);
    },
    // signalContext is derived from stable props; omitted to avoid re-creating the callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentDocument.index, currentDocument.file, currentDocument.docId, onSave]
  );

  // The carousel reuses this component instance across documents — reset the
  // once-per-form guard when the document changes so each document logs once.
  useEffect(() => {
    hasLoggedBasePayRef.current = false;
  }, [currentDocument.docId]);

  const payPeriodStartDate = methods.watch('payPeriodStartDate');

  useEffect(() => {
    if (payPeriodStartDate !== '' && payPeriodStartDate !== undefined) {
      handleOnSave(methods.getValues());
    }
  }, [payPeriodStartDate, handleOnSave, methods.getValues]);

  const payPeriodEndDate = methods.watch('payPeriodEndDate');
  const documentType = methods.watch('type');

  const isAutomatedProof = currentDocument.isAutomated === true;
  const isDisabledForm =
    isAutomatedProof || isExcluded || !documentType || !eligibleDocuments.includes(documentType);

  const onDocumentTypeChange = useCallback(
    ({
      type: {
        selected: { value: selectedDocumentType }
      }
    }: {
      type: { selected: docTypeItem };
    }) => {
      if (!eligibleDocuments.includes(selectedDocumentType)) {
        clearIVFormFields(methods.setValue);
        goToNextDocument();
      }
      const values = methods.getValues();
      handleOnSave(values);
    },
    [goToNextDocument, handleOnSave, methods.setValue, methods.getValues]
  );

  useEffect(() => {
    if (payPeriodEndDate !== '' && payPeriodEndDate !== undefined) {
      handleOnSave(methods.getValues());
    }
  }, [payPeriodEndDate, handleOnSave, methods.getValues]);

  return (
    <Row gap={1}>
      <PdfViewerWrapper size={3.5}>
        <PDFViewer
          id={currentDocument.id}
          file={currentDocument.file}
          fileName={currentDocument.fileName}
          embedMode={EmbedMode.SIZED_CONTAINER}
          height="85vh"
          showZoomControl
        ></PDFViewer>
      </PdfViewerWrapper>
      <Row.Col size={3}>
        <Text isBold={true} align={TextAlign.center} variant={TextVariant.h3}>
          Doc ID: {currentDocument.docId}
        </Text>
        <Gap height={0.25} />
        <Text isBold={true} align={TextAlign.center}>
          Document {currentDocument.index + 1} of {totalNumberOfDocuments}
        </Text>
        <Gap height={2} />
        {hasMismatch && severity && (
          <>
            <CarouselMismatchBanner
              severity={severity}
              role={severity === 'error' ? 'alert' : 'status'}
              data-testid={`mismatch-banner-${currentDocument.docId}`}
            >
              <Icon
                icon={severity === 'error' ? 'icon-error' : 'icon-warning'}
                color={severity === 'error' ? 'red' : 'yellow'}
              />
              <div>
                <strong>Name Mismatch:</strong> Excluded from calculation.
                {applicantName && documentName && (
                  <NameComparison>
                    <span>&quot;{applicantName}&quot;</span>
                    <span>vs</span>
                    <span>&quot;{documentName}&quot;</span>
                  </NameComparison>
                )}
              </div>
            </CarouselMismatchBanner>
            {onToggleInclude && (
              <CarouselIncludeRow data-testid={`include-row-${currentDocument.docId}`}>
                <Checkbox
                  name={`carousel-include-${currentDocument.docId}`}
                  value={currentDocument.docId}
                  label="Include in calculation"
                  isChecked={isIncludedInCalculation ?? false}
                  onClick={() => onToggleInclude()}
                  showLabel={true}
                />
              </CarouselIncludeRow>
            )}
          </>
        )}
        <Gap height={hasMismatch ? 1 : 2} />
        <FormProvider {...methods}>
          <form onBlur={methods.handleSubmit(handleOnSave)}>
            <Container>
              <Row gap={3}>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <Text align={TextAlign.left}>Document Type</Text>
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content="Verify the document type matches the document you're reviewing. Update if necessary."
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                  <Gap height={0.5} />
                  <Row className="raise">
                    <Row.Col size={4}>
                      <ControlledDropdown
                        name="type"
                        data={proofDocumentTypes}
                        showLabel={false}
                        isDisabled={isDisabledForm || isAutomatedProof}
                        onChange={onDocumentTypeChange}
                      />
                    </Row.Col>
                  </Row>
                </Row.Col>
                <Row.Col size={4}>
                  <div></div>
                </Row.Col>
              </Row>
              <Gap height={1.7} />
              <Row gap={3}>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <Text align={TextAlign.left}>Pay Period Start Date</Text>
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content="Enter the first day of the pay period noted on the document. You may also see this as 'period start', 'period starting', 'period beginning', or 'pay period' with a given range."
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                  <Gap height={0.5} />
                  <Row>
                    <ControlledDatePicker
                      className="income-verification-date-picker"
                      name="payPeriodStartDate"
                      selectsStart
                      disabled={isDisabledForm}
                    />
                  </Row>
                </Row.Col>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <Text align={TextAlign.left}>Pay Period End Date</Text>
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content="Enter the last/final day of the pay period noted on the document. You may also see this as 'period end', 'period ending', 'pay end date', or 'pay period' with a given range."
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                  <Gap height={0.5} />
                  <Row>
                    <ControlledDatePicker
                      className="income-verification-date-picker"
                      name="payPeriodEndDate"
                      selectsStart
                      disabled={isDisabledForm}
                    />
                  </Row>
                </Row.Col>
              </Row>
              <Row gap={3}>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <Text align={TextAlign.left}>Base Pay</Text>
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content='Enter the regular/base earnings shown on the paystub for this pay period (e.g. "Regular Hours," "Base Pay," or equivalent core income line). Do not include bonuses or other extras.'
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                  <Gap height={0.5} />
                  <Row>
                    <Row.Col size={12}>
                      <InputText
                        name="basePayIncomeAmount"
                        data-testid={`base-pay-income-amount-${currentDocument.docId}`}
                        type={InputTextType.text}
                        placeholder=""
                        showOptionalLabel={false}
                        isDisabled={isDisabledForm}
                      />
                    </Row.Col>
                  </Row>
                </Row.Col>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <Text align={TextAlign.left}>Gross Pay</Text>
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content="Enter the total amount of income earned before deductions or taxes. This includes salary, wages, bonuses, and any other forms of compensation before any deductions are taken out. You may also see this amount under 'total gross', 'total', or 'gross benefit amount'."
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                  <Gap height={0.5} />
                  <Row>
                    <Row.Col size={12}>
                      <InputText
                        name="grossPay"
                        type={InputTextType.text}
                        placeholder=""
                        defaultValue={currentDocument.grossPay}
                        showOptionalLabel={false}
                        isDisabled={isDisabledForm}
                      ></InputText>
                    </Row.Col>
                  </Row>
                </Row.Col>
              </Row>
              <Row gap={3}>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <Text align={TextAlign.left}>Recipient Name</Text>
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content="Enter the name of the individual or entity to whom the document is addressed. You may find this under 'Employee Name', 'Payee Name', 'Account Holder Name', 'Beneficiary', etc."
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                  <Gap height={0.5} />
                  <Row>
                    <Row.Col size={12}>
                      <InputText
                        name="recipientName"
                        type={InputTextType.text}
                        placeholder=""
                        defaultValue={currentDocument.recipientName}
                        showOptionalLabel={false}
                        isDisabled={isDisabledForm}
                      ></InputText>
                    </Row.Col>
                  </Row>
                </Row.Col>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <Text align={TextAlign.left}>Income Source</Text>
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content="Enter the payer noted in the document. This may be listed near the top of the document, typically alongside your personal information. You may also find it under 'Employer Name', 'Employer Information', 'Benefits Program', 'Organization', etc."
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                  <Gap height={0.5} />
                  <Row>
                    <Row.Col size={12}>
                      <InputText
                        name="incomeSource"
                        type={InputTextType.text}
                        placeholder=""
                        defaultValue={currentDocument.incomeSource}
                        showOptionalLabel={false}
                        isDisabled={isDisabledForm}
                      ></InputText>
                    </Row.Col>
                  </Row>
                </Row.Col>
              </Row>
              <Row gap={3}>
                <Row.Col size={4}>
                  <Row>
                    <Row.Col>
                      <ControlledCheckbox
                        name="hasNonRecurringIncome"
                        data-testid={`nri-checkbox-${currentDocument.docId}`}
                        label="Non-Recurring Income Present"
                        onClick={(data) => {
                          logNriCheckboxToggled(data.isChecked, signalContext);
                          handleOnSave({
                            ...methods.getValues(),
                            hasNonRecurringIncome: data.isChecked
                          });
                        }}
                        showLabel={true}
                        isDisabled={isDisabledForm}
                      />
                    </Row.Col>
                    <Row.Col>
                      <ToolTip
                        direction={ToolTipDirection.top}
                        content={`Check this box only if the paystub shows a line item explicitly labeled "Bonus" for the current pay period. You don't need to identify any other non-recurring income types.`}
                      >
                        <Icon
                          className="income-verification-tooltip"
                          color={IconColor.blue700}
                          icon="help"
                        />
                      </ToolTip>
                    </Row.Col>
                  </Row>
                </Row.Col>
                <Row.Col size={4}>
                  <div></div>
                </Row.Col>
              </Row>
            </Container>
          </form>
        </FormProvider>
      </Row.Col>
    </Row>
  );
};

export default IncomeVerificationForm;
